import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Loader2, Users, ChevronDown, ChevronRight, FileText, FileSpreadsheet } from 'lucide-react';
import jsPDF from 'jspdf';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import TabPageLayout, { DataCard } from './TabPageLayout';
import EmptyState from './EmptyState';
import { BACKEND_URL } from '../config/api';
import { logAdminEvent, formatTripDate, formatTripDateShort } from '../utils/superAdmin';

const PLATFORM_API = `${BACKEND_URL}/api/v2/super-admin`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

const formatCurrency = (amount, currency = 'USD') => {
  const num = Number(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const firstOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
};
const today = () => new Date().toISOString().split('T')[0];

const SuperAdminReportsTab = () => {
  const [reportStart, setReportStart] = useState(firstOfMonth());
  const [reportEnd, setReportEnd] = useState(today());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedOrgs, setExpandedOrgs] = useState(new Set());

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${PLATFORM_API}/partner-report?start_date=${reportStart}&end_date=${reportEnd}&include_bookings=true`,
        authHeaders()
      );
      setReport(res.data);
      setExpandedOrgs(new Set());
    } catch (err) {
      toast.error('Failed to load payout report');
    } finally {
      setLoading(false);
    }
  }, [reportStart, reportEnd]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const toggleOrg = (key) => {
    setExpandedOrgs(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const applyPreset = (preset) => {
    const now = new Date();
    if (preset === 'thisMonth') {
      setReportStart(firstOfMonth());
      setReportEnd(today());
    } else if (preset === 'lastMonth') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      setReportStart(start.toISOString().split('T')[0]);
      setReportEnd(end.toISOString().split('T')[0]);
    } else if (preset === 'last30') {
      setReportStart(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
      setReportEnd(today());
    }
  };

  const exportCSV = () => {
    if (!report?.partners?.length) { toast.error('No report data to export'); return; }
    const rows = [
      ['BOOKINGFLOWS TEAM PAYOUT REPORT'],
      [`Period: ${report.start_date} to ${report.end_date}`],
      [`Generated: ${new Date().toISOString()}`],
      [''],
    ];
    report.partners.forEach(p => {
      rows.push([`TEAM MEMBER: ${p.name || p.username}`, `Commission: ${p.commission_rate}%`]);
      rows.push(['Company', 'Booking Code', 'Trip Date', 'Currency', 'Total', 'Platform Fee', 'Member Share']);
      p.organizations.forEach(o => {
        (o.bookings_detail || []).forEach(b => {
          rows.push([`"${o.name}"`, b.code, b.date, o.currency, b.total.toFixed(2), b.fee.toFixed(2), b.share.toFixed(2)]);
        });
        rows.push([`"${o.name} — SUBTOTAL"`, '', '', o.currency, o.gmv.toFixed(2), o.fees.toFixed(2), o.partner_share.toFixed(2)]);
      });
      p.totals.forEach(t => {
        rows.push([`TOTAL (${t.currency})`, '', '', t.currency, t.gmv.toFixed(2), t.fees.toFixed(2), t.partner_share.toFixed(2)]);
      });
      rows.push(['']);
    });
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-payouts-${report.start_date}-to-${report.end_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    logAdminEvent('report_exported', { report: 'team_payouts', format: 'csv' });
    toast.success('Payout report exported');
  };

  const exportPDF = () => {
    if (!report?.partners?.length) { toast.error('No report data to export'); return; }
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('BookingFlows — Team Payout Report', pageWidth / 2, y, { align: 'center' });
    y += 7;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(`${report.start_date} to ${report.end_date}  •  Generated ${new Date().toLocaleDateString()}`, pageWidth / 2, y, { align: 'center' });
    y += 12;
    doc.setTextColor(0);

    report.partners.forEach(p => {
      if (y > 260) { doc.addPage(); y = 20; }
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${p.name || p.username} — ${p.commission_rate}% of platform fees`, 14, y);
      y += 7;

      p.organizations.forEach(o => {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(o.name, 16, y);
        doc.setFont('helvetica', 'normal');
        doc.text(`${o.bookings} bookings  •  Fees ${formatCurrency(o.fees, o.currency)}  •  Share ${formatCurrency(o.partner_share, o.currency)}`, 90, y);
        y += 6;

        doc.setFontSize(8);
        (o.bookings_detail || []).forEach(b => {
          if (y > 280) { doc.addPage(); y = 20; }
          doc.setTextColor(90);
          doc.text(`${b.code}  •  ${formatTripDateShort(b.date)}`, 20, y);
          doc.text(`Total ${formatCurrency(b.total, o.currency)}   Fee ${formatCurrency(b.fee, o.currency)}   Share ${formatCurrency(b.share, o.currency)}`, 95, y);
          doc.setTextColor(0);
          y += 4.5;
        });
        y += 3;
      });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      p.totals.forEach(t => {
        if (y > 275) { doc.addPage(); y = 20; }
        doc.text(`TOTAL OWED (${t.currency}): ${formatCurrency(t.partner_share, t.currency)}`, 16, y);
        y += 6;
      });
      y += 6;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(150);
      doc.text('BookingFlows — Confidential', 14, 290);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
    }

    doc.save(`team-payouts-${report.start_date}-to-${report.end_date}.pdf`);
    logAdminEvent('report_exported', { report: 'team_payouts', format: 'pdf' });
    toast.success('PDF exported');
  };

  return (
    <TabPageLayout
      title="Reports"
      subtitle="Team payout reports — each member's share of platform fees, traceable to real bookings"
      loading={loading && !report}
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9" data-testid="payout-export-button">
              <Download className="w-4 h-4 mr-1" />Export<ChevronDown className="w-3 h-3 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={exportPDF} data-testid="payout-export-pdf">
              <FileText className="w-4 h-4 mr-2" />PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={exportCSV} data-testid="payout-export-csv">
              <FileSpreadsheet className="w-4 h-4 mr-2" />CSV
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      <div className="space-y-6" data-testid="payout-report-section">
        <DataCard title="Team Payouts" subtitle="Member share = their % of the platform fees generated by companies they onboarded">
          <div className="space-y-5">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">From</Label>
                <Input type="date" value={reportStart} onChange={(e) => setReportStart(e.target.value)} className="h-9 w-40" data-testid="payout-start-date" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">To</Label>
                <Input type="date" value={reportEnd} onChange={(e) => setReportEnd(e.target.value)} className="h-9 w-40" data-testid="payout-end-date" />
              </div>
              <div className="flex gap-2 pb-0.5">
                {[
                  { key: 'thisMonth', label: 'This Month' },
                  { key: 'lastMonth', label: 'Last Month' },
                  { key: 'last30', label: 'Last 30 Days' },
                ].map(p => (
                  <button
                    key={p.key}
                    onClick={() => applyPreset(p.key)}
                    className="px-3 py-1.5 text-xs font-medium rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                    data-testid={`payout-preset-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : report?.partners?.length > 0 ? (
              <div className="space-y-6">
                {report.partners.map(partner => (
                  <div key={partner.admin_id} className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`payout-partner-${partner.username}`}>
                    <div className="px-4 py-3 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold text-gray-900">{partner.name || partner.username}</p>
                        <p className="text-xs text-gray-500">
                          {partner.org_count} {partner.org_count === 1 ? 'company' : 'companies'} • {partner.commission_rate}% of platform fees
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {partner.totals.length > 0 ? partner.totals.map(t => (
                          <span key={t.currency} className="inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-sm">
                            <span className="font-semibold text-gray-900">{formatCurrency(t.partner_share, t.currency)}</span>
                            <span className="text-xs text-gray-500">owed ({t.currency})</span>
                          </span>
                        )) : (
                          <span className="text-xs text-gray-400">No companies assigned</span>
                        )}
                      </div>
                    </div>
                    {partner.organizations.length > 0 && (
                      <div className="divide-y divide-gray-100">
                        {partner.organizations.map(org => {
                          const key = `${partner.admin_id}-${org.id}`;
                          const isExpanded = expandedOrgs.has(key);
                          const detail = org.bookings_detail || [];
                          return (
                            <div key={org.id}>
                              <button
                                onClick={() => detail.length && toggleOrg(key)}
                                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${detail.length ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'}`}
                                data-testid={`payout-org-row-${org.slug}`}
                              >
                                {detail.length > 0 ? (
                                  isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                ) : <span className="w-4 flex-shrink-0" />}
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900">{org.name}</p>
                                  <p className="text-xs text-gray-400">{org.bookings} {org.bookings === 1 ? 'booking' : 'bookings'}{detail.length ? ' — click to view' : ''}</p>
                                </div>
                                <div className="text-right text-sm whitespace-nowrap">
                                  <span className="text-gray-500 mr-4 hidden sm:inline">GMV {formatCurrency(org.gmv, org.currency)}</span>
                                  <span className="text-gray-500 mr-4">Fees {formatCurrency(org.fees, org.currency)}</span>
                                  <span className="font-semibold text-gray-900">{formatCurrency(org.partner_share, org.currency)}</span>
                                </div>
                              </button>
                              {isExpanded && detail.length > 0 && (
                                <div className="bg-gray-50/60 px-4 pb-3" data-testid={`payout-bookings-detail-${org.slug}`}>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-gray-400">
                                        <th className="py-2 pl-7 text-left font-medium">Booking</th>
                                        <th className="py-2 text-left font-medium">Trip Date</th>
                                        <th className="py-2 text-right font-medium">Total</th>
                                        <th className="py-2 text-right font-medium">Platform Fee</th>
                                        <th className="py-2 text-right font-medium">Member Share</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {detail.map(b => (
                                        <tr key={b.code}>
                                          <td className="py-2 pl-7 font-mono text-gray-900">{b.code}</td>
                                          <td className="py-2 text-gray-600">{formatTripDate(b.date)}</td>
                                          <td className="py-2 text-right text-gray-600">{formatCurrency(b.total, org.currency)}</td>
                                          <td className="py-2 text-right text-gray-600">{formatCurrency(b.fee, org.currency)}</td>
                                          <td className="py-2 text-right font-medium text-gray-900">{formatCurrency(b.share, org.currency)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={Users}
                title="No team members with companies yet"
                description="Add a team member with a commission rate and assign them companies (Team tab) — their payout will show up here."
                showAction={false}
              />
            )}
          </div>
        </DataCard>
      </div>
    </TabPageLayout>
  );
};

export default SuperAdminReportsTab;
